import {
  Announcements,
  CollisionDetection,
  DragEndEvent,
  DragMoveEvent,
  DragOverEvent,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  ScreenReaderInstructions,
  UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {Coordinates} from '@dnd-kit/utilities';
import {match, Pattern} from 'ts-pattern';

import {useRef, useState} from 'react';

import {equals, isNil, isNotNil} from 'ramda';

import i18n from '@dms/i18n';

import {DashboardDropIntent} from '../types/DashboardDropIntent';
import {DashboardTile} from '../types/DashboardTile';
import {DashboardTileCoordinates} from '../types/DashboardTileCoordinates';
import {DashboardTileId} from '../types/DashboardTileId';
import {createDashboardKeyboardSorting} from '../utils/dashboardKeyboardSorting';
import {getDashboardDragDestinationId} from '../utils/getDashboardDragDestinationId';
import {getDashboardDropPreview} from '../utils/getDashboardDropPreview';
import {getDashboardGapSlots} from '../utils/getDashboardGapSlots';
import {getDashboardTileLabel} from '../utils/getDashboardTileLabel';
import {isDashboardTileFullWidth} from '../utils/isDashboardTileFullWidth';
import {isDashboardTileId} from '../utils/isDashboardTileId';
import {parseDashboardGapSlotId} from '../utils/parseDashboardGapSlotId';
import {
  DashboardDropTransaction,
  projectDashboardDrop,
  projectDashboardGapDrop,
} from '../utils/projectDashboardDrop';
import {resolveTileDropPlacement} from '../utils/resolveTileDropPlacement';

interface UseDashboardDragAndDropOptions {
  tiles: DashboardTile[];
  layoutSignature: string;
  onDrop: (transaction: DashboardDropTransaction) => void;
  onDraggingChange: (isDragging: boolean) => void;
}

interface DashboardDropFeedback {
  intent: DashboardDropIntent | null;
  invalidTarget: DashboardTile | null;
}

type DragUpdateEvent = DragMoveEvent | DragOverEvent | DragEndEvent;

const DRAG_ACTIVATION_DISTANCE = 10;
const FIRST_POSITION = 1;
const NO_DROP_FEEDBACK: DashboardDropFeedback = {intent: null, invalidTarget: null};

// A free place either takes the active tile alone or offers nothing; it never shows an invalid ghost.
const resolveGapDropFeedback = (
  tiles: DashboardTile[],
  activeId: DashboardTileId,
  overId: string,
  gap: DashboardTileCoordinates
): DashboardDropFeedback => {
  const transaction = projectDashboardGapDrop(tiles, activeId, gap);

  return transaction === null
    ? NO_DROP_FEEDBACK
    : {intent: {overId, placement: 'swap', transaction}, invalidTarget: null};
};

export function useDashboardDragAndDrop(options: UseDashboardDragAndDropOptions) {
  const [activeTileId, setActiveTileId] = useState<DashboardTileId | null>(null);
  const [dropIntent, setDropIntent] = useState<DashboardDropIntent | null>(null);
  const [invalidTarget, setInvalidTarget] = useState<DashboardTile | null>(null);
  const dropIntentRef = useRef<DashboardDropIntent | null>(null);
  const completedDropPositionRef = useRef<number | null>(null);
  const droppableRectsRef = useRef<Parameters<CollisionDetection>[0]['droppableRects']>(new Map());
  const pointerCoordinatesRef = useRef<Coordinates | null>(null);
  const [keyboardSorting] = useState(createDashboardKeyboardSorting);

  keyboardSorting.setTiles(options.tiles);

  const collisionDetection: CollisionDetection = (args) => {
    droppableRectsRef.current = args.droppableRects;
    pointerCoordinatesRef.current = args.pointerCoordinates;

    return keyboardSorting.collisionDetection(args);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {activationConstraint: {distance: DRAG_ACTIVATION_DISTANCE}}),
    useSensor(KeyboardSensor, {coordinateGetter: keyboardSorting.coordinateGetter})
  );

  const activeTile = options.tiles.find((tile) => tile.id === activeTileId) ?? null;

  const getTilePosition = (id: UniqueIdentifier) =>
    options.tiles.findIndex((tile) => tile.id === id) + FIRST_POSITION;

  const resolveDropFeedback = (event: DragUpdateEvent): DashboardDropFeedback => {
    const activeId = event.active.id.toString();
    const point = pointerCoordinatesRef.current;
    const destinationId = getDashboardDragDestinationId({
      collisions: event.collisions,
      keyboardOverId: keyboardSorting.getOverId(),
      pointerCoordinates: point,
    });
    const overId = destinationId?.toString();

    if (!isDashboardTileId(activeId) || isNil(overId)) {
      return NO_DROP_FEEDBACK;
    }

    const gap = parseDashboardGapSlotId(overId);

    if (gap !== null) {
      return resolveGapDropFeedback(options.tiles, activeId, overId, gap);
    }

    if (!isDashboardTileId(overId) || activeId === overId) {
      return NO_DROP_FEEDBACK;
    }

    const activeTileAtIndex = options.tiles.find((tile) => tile.id === activeId);
    const overTileAtIndex = options.tiles.find((tile) => tile.id === overId);
    const overRect = droppableRectsRef.current.get(overId);

    if (isNil(activeTileAtIndex) || isNil(overTileAtIndex) || isNil(overRect)) {
      return NO_DROP_FEEDBACK;
    }

    const placement = match(point)
      .with(Pattern.nonNullable, (dragPoint) =>
        resolveTileDropPlacement({
          activeSize: activeTileAtIndex.size,
          overId,
          tiles: options.tiles,
          rects: droppableRectsRef.current,
          overRect,
          point: dragPoint,
        })
      )
      .otherwise(() => keyboardSorting.getPlacement());

    if (isNil(placement)) {
      return NO_DROP_FEEDBACK;
    }

    const transaction = projectDashboardDrop(options.tiles, activeId, overId, placement);

    if (transaction !== null) {
      return {intent: {overId, placement, transaction}, invalidTarget: null};
    }

    const isNarrowDrag = !isDashboardTileFullWidth(activeTileAtIndex.size);

    return {intent: null, invalidTarget: isNarrowDrag ? overTileAtIndex : null};
  };

  const updateDropFeedback = (event: DragUpdateEvent) => {
    const feedback = resolveDropFeedback(event);
    dropIntentRef.current = feedback.intent;
    setDropIntent((current) => (equals(current, feedback.intent) ? current : feedback.intent));
    setInvalidTarget((current) =>
      equals(current, feedback.invalidTarget) ? current : feedback.invalidTarget
    );
  };

  const clearDragState = () => {
    dropIntentRef.current = null;
    setActiveTileId(null);
    setDropIntent(null);
    setInvalidTarget(null);
    pointerCoordinatesRef.current = null;
    keyboardSorting.reset();
  };

  const screenReaderInstructions: ScreenReaderInstructions = {
    draggable: i18n.t('page.homepage.dashboardLayout.a11y.instructions'),
  };

  const announcements: Announcements = {
    onDragStart: ({active}) =>
      i18n.t('page.homepage.dashboardLayout.a11y.pickedUp', {
        tile: getDashboardTileLabel(active.id),
        position: getTilePosition(active.id),
        count: options.tiles.length,
      }),
    onDragOver: ({active}) => {
      const projectedIndex = keyboardSorting.getProjectedIndex();

      if (isNil(projectedIndex)) {
        return undefined;
      }

      return i18n.t('page.homepage.dashboardLayout.a11y.moved', {
        tile: getDashboardTileLabel(active.id),
        position: projectedIndex + FIRST_POSITION,
        count: options.tiles.length,
      });
    },
    onDragEnd: ({active}) =>
      i18n.t('page.homepage.dashboardLayout.a11y.dropped', {
        tile: getDashboardTileLabel(active.id),
        position: completedDropPositionRef.current ?? getTilePosition(active.id),
        count: options.tiles.length,
      }),
    onDragCancel: ({active}) =>
      i18n.t('page.homepage.dashboardLayout.a11y.cancelled', {
        tile: getDashboardTileLabel(active.id),
        position: getTilePosition(active.id),
        count: options.tiles.length,
      }),
  };

  const handleDragStart = (event: DragStartEvent) => {
    const id = event.active.id.toString();

    if (isDashboardTileId(id)) {
      completedDropPositionRef.current = null;
      keyboardSorting.reset();
      pointerCoordinatesRef.current = null;
      dropIntentRef.current = null;
      options.onDraggingChange(true);
      setActiveTileId(id);
      setDropIntent(null);
      setInvalidTarget(null);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const finalOverId = getDashboardDragDestinationId({
      collisions: event.collisions,
      keyboardOverId: keyboardSorting.getOverId(),
      pointerCoordinates: pointerCoordinatesRef.current,
    })?.toString();
    const intent = dropIntentRef.current;

    if (
      isNotNil(intent) &&
      finalOverId === intent.overId &&
      intent.transaction.baseLayoutSignature === options.layoutSignature
    ) {
      completedDropPositionRef.current = intent.transaction.landingIndex + FIRST_POSITION;
      options.onDrop(intent.transaction);
    } else {
      completedDropPositionRef.current = getTilePosition(event.active.id);
    }

    clearDragState();
    options.onDraggingChange(false);
  };

  const handleDragCancel = () => {
    completedDropPositionRef.current = null;
    clearDragState();
    options.onDraggingChange(false);
  };

  return {
    contextProps: {
      sensors,
      collisionDetection,
      accessibility: {announcements, screenReaderInstructions},
      onDragStart: handleDragStart,
      onDragMove: updateDropFeedback,
      onDragOver: updateDropFeedback,
      onDragEnd: handleDragEnd,
      onDragCancel: handleDragCancel,
    },
    activeTile,
    gapSlots: activeTile === null ? [] : getDashboardGapSlots(options.tiles, activeTile.id),
    invalidTarget,
    preview: getDashboardDropPreview(dropIntent, activeTileId, options.tiles),
  };
}
